import { onDestroy, onMount } from "@/modules/_";
import { Raf, Resize } from "@lib/subs";
import gsap, { reduced } from "@lib/gsap";
import { TIMEZONES, getTimeInZone, type TimezoneKey } from "@utils/time";

const PILL_DURATION = 0.6;
const PILL_EASE = "expo.out";
const VISUAL_DURATION = 1;
const VISUAL_EASE = "expo.inOut";
const VISUAL_IMG_SCALE_FROM = 2;
const VISUAL_IMG_SCALE_DURATION = 2;
const VISUAL_IMG_SCALE_EASE = "expo.out";
const VISUAL_IMG_SCALE_DELAY = 0;
const PREV_FINISH_DURATION = 0.35;
const PREV_FINISH_EASE = "power2.out";
const GROUP_BG = "#1b1a18";
const AUTO_INTERVAL_MS = 10_000;
const ICON_DURATION = 0.55;
const ICON_EASE = "power2.out";
const ICON_SCALE_OUT = 0.6;
const ICON_BLUR_OUT = 4;
const INFO_DURATION = 1.6;
const INFO_EASE = "expo.out";
const INFO_STACK_SCALE_STEP = 0.06;
const INFO_STACK_PEEK = 8;
const INFO_SHADOW = "0 2px 10px rgba(0, 0, 0, 0.1)";
const COLON_DIM = 0.25;
const COLON_HALF_DURATION = 0.25;

type Clock = {
  timeZone: string;
  setHour: (v: number) => void;
  setMinute: (v: number) => void;
  setSecond?: (v: number) => void;
};

type Direction = "left" | "right";

type VisualsAPI = {
  setInitial: (loc: string) => void;
  reveal: (loc: string, direction: Direction) => void;
  getPanelEl: (loc: string) => HTMLElement | undefined;
};

type InfoAPI = {
  tick: (now: Date) => void;
  destroy: () => void;
  setInitial: (loc: string) => void;
  swap: (loc: string, direction: Direction) => void;
};

export default function (element: HTMLElement) {
  const clocks: Clock[] = [];
  const cleanups: Array<() => void> = [];
  let stopRaf: (() => void) | undefined;

  function tickClocks() {
    // One Date shared across all clocks keeps them in sync and avoids one allocation per clock per frame.
    const now = new Date();
    for (const c of clocks) {
      const { hour, minute, second, ms } = getTimeInZone(c.timeZone, now);
      const fSecond = second + ms / 1000;
      const fMinute = minute + fSecond / 60;
      const fHour = (hour % 12) + fMinute / 60;
      c.setMinute(fMinute * 6);
      c.setHour(fHour * 30);
      c.setSecond?.(fSecond * 6);
    }
  }

  function setupClock(c: HTMLElement) {
    const tzKey = c.getAttribute("timezone") as TimezoneKey | null;
    const timeZone = tzKey ? TIMEZONES[tzKey] : undefined;
    if (!timeZone) return;

    const hour = c.querySelector<HTMLElement>('[office="clock-short-hand"]');
    const minute = c.querySelector<HTMLElement>('[office="clock-long-hand"]');
    const second = c.querySelector<HTMLElement>('[office="clock-sec-hand"]');
    const dot = c.querySelector<HTMLElement>('[office="clock-dot"]');
    if (!hour || !minute) return;

    const hands = [hour, minute, ...(second ? [second] : [])];
    gsap.set(hands, {
      left: "50%",
      xPercent: -50,
      transformOrigin: "50% 100%",
    });

    gsap.set(hour, { zIndex: 1 });
    gsap.set(minute, { zIndex: 2 });
    if (second) gsap.set(second, { zIndex: 3 });
    if (dot) gsap.set(dot, { zIndex: 4 });

    for (const el of [...hands, dot]) {
      if (el) el.setAttribute("aria-hidden", "true");
    }

    clocks.push({
      timeZone,
      setHour: gsap.quickSetter(hour, "rotate", "deg") as (v: number) => void,
      setMinute: gsap.quickSetter(minute, "rotate", "deg") as (
        v: number,
      ) => void,
      setSecond: second
        ? (gsap.quickSetter(second, "rotate", "deg") as (v: number) => void)
        : undefined,
    });
  }

  function setupInfo(infoM: HTMLElement): InfoAPI {
    type InfoBinding = {
      loc: string;
      item: HTMLElement;
      hhEl: HTMLElement;
      mmEl: HTMLElement;
      colonEl: HTMLElement;
      tzEl: HTMLElement | null;
      dayEl: HTMLElement | null;
      nightEl: HTMLElement | null;
      lastTime: string;
      lastTz: string;
      lastIsDay: boolean | null;
    };

    const bindings: InfoBinding[] = [];
    const colonTweens: gsap.core.Tween[] = [];
    const items = Array.from(
      infoM.querySelectorAll<HTMLElement>('[office="info-item"]'),
    );

    for (const item of items) {
      const loc = item.getAttribute("timezone");
      if (!loc) continue;
      const timeEl = item.querySelector<HTMLElement>('[office="time-text"]');
      if (!timeEl) continue;

      gsap.set(item, { boxShadow: INFO_SHADOW });

      timeEl.innerHTML = "<span></span><span>:</span><span></span>";
      const [hhEl, colonEl, mmEl] = Array.from(
        timeEl.children,
      ) as HTMLElement[];

      const dayEl = item.querySelector<HTMLElement>('[office="time-icon-day"]');
      const nightEl = item.querySelector<HTMLElement>(
        '[office="time-icon-night"]',
      );
      const iconWrap = item.querySelector<HTMLElement>(
        '[office="time-icon-w"]',
      );

      if (iconWrap) gsap.set(iconWrap, { position: "relative" });
      if (dayEl) gsap.set(dayEl, { position: "absolute", inset: 0 });
      if (nightEl) gsap.set(nightEl, { position: "absolute", inset: 0 });

      if (!reduced) {
        colonTweens.push(
          gsap.to(colonEl, {
            opacity: COLON_DIM,
            duration: COLON_HALF_DURATION,
            ease: "sine.inOut",
            repeat: -1,
            yoyo: true,
          }),
        );
      }

      bindings.push({
        loc,
        item,
        hhEl,
        mmEl,
        colonEl,
        tzEl: item.querySelector<HTMLElement>('[office="timezone-text"]'),
        dayEl,
        nightEl,
        lastTime: "",
        lastTz: "",
        lastIsDay: null,
      });
    }

    const pad = (n: number) => (n < 10 ? `0${n}` : String(n));

    function setIconState(b: InfoBinding, isDay: boolean, animate: boolean) {
      const incoming = isDay ? b.dayEl : b.nightEl;
      const outgoing = isDay ? b.nightEl : b.dayEl;
      const inVars = { opacity: 1, scale: 1, filter: "blur(0px)" };
      const outVars = {
        opacity: 0,
        scale: ICON_SCALE_OUT,
        filter: `blur(${ICON_BLUR_OUT}px)`,
      };

      if (!animate || reduced) {
        if (incoming) gsap.set(incoming, inVars);
        if (outgoing) gsap.set(outgoing, outVars);
        return;
      }
      if (incoming)
        gsap.to(incoming, {
          ...inVars,
          duration: ICON_DURATION,
          ease: ICON_EASE,
        });
      if (outgoing)
        gsap.to(outgoing, {
          ...outVars,
          duration: ICON_DURATION,
          ease: ICON_EASE,
        });
    }

    function tick(now: Date) {
      for (const b of bindings) {
        const { hour, minute, tzAbbr } = getTimeInZone(b.loc, now);

        const time = `${pad(hour)}:${pad(minute)}`;
        if (time !== b.lastTime) {
          b.hhEl.textContent = pad(hour);
          b.mmEl.textContent = pad(minute);
          b.lastTime = time;
        }

        if (b.tzEl && tzAbbr !== b.lastTz) {
          b.tzEl.textContent = tzAbbr;
          b.lastTz = tzAbbr;
        }

        // lastIsDay null on first tick → snap; subsequent changes animate.
        const isDay = hour >= 6 && hour < 18;
        if (isDay !== b.lastIsDay) {
          setIconState(b, isDay, b.lastIsDay !== null);
          b.lastIsDay = isDay;
        }
      }
    }

    let activeLoc: string | undefined;
    let swapTl: gsap.core.Timeline | undefined;
    let itemHeight = 0;

    // Two-pass measure: widths first (natural content), then heights (after widths applied, because narrower cards wrap text taller).
    function measure() {
      for (const b of bindings) {
        gsap.set(b.item, { clearProps: "width,height" });
      }
      let maxW = 0;
      for (const b of bindings) {
        const w = b.item.offsetWidth;
        if (w > maxW) maxW = w;
      }
      for (const b of bindings) {
        gsap.set(b.item, { width: maxW });
      }
      let maxH = 0;
      for (const b of bindings) {
        const h = b.item.offsetHeight;
        if (h > maxH) maxH = h;
      }
      itemHeight = maxH;
      for (const b of bindings) {
        gsap.set(b.item, { height: maxH });
      }
    }

    // scale collapses toward center by h*(1-scale)/2; translate compensates so the bottom edges step by exactly INFO_STACK_PEEK.
    function stackVars(pos: number) {
      const scale = 1 - INFO_STACK_SCALE_STEP * pos;
      return {
        scale,
        y: (itemHeight * (1 - scale)) / 2 + INFO_STACK_PEEK * pos,
        zIndex: bindings.length - pos,
      };
    }

    function stackOrder(loc: string): InfoBinding[] {
      const idx = bindings.findIndex((b) => b.loc === loc);
      if (idx === -1) return bindings.slice();
      return [...bindings.slice(idx), ...bindings.slice(0, idx)];
    }

    function applyStack(loc: string, tl: gsap.core.Timeline | null) {
      const ordered = stackOrder(loc);

      // z-index snaps immediately so the incoming item paints on top for the full duration of the tween.
      for (let i = 0; i < ordered.length; i++) {
        gsap.set(ordered[i].item, { zIndex: bindings.length - i });
      }

      for (let i = 0; i < ordered.length; i++) {
        const b = ordered[i];
        const isActive = i === 0;
        const vars = {
          ...stackVars(i),
          pointerEvents: isActive ? "auto" : "none",
        };
        if (tl) {
          tl.to(b.item, vars, 0);
        } else {
          gsap.set(b.item, vars);
        }
      }
    }

    function setInitial(loc: string) {
      measure();
      applyStack(loc, null);
      activeLoc = loc;
    }

    function swap(loc: string, _direction: Direction) {
      if (loc === activeLoc) return;
      if (!bindings.find((b) => b.loc === loc)) return;

      swapTl?.kill();
      if (reduced) {
        applyStack(loc, null);
      } else {
        swapTl = gsap.timeline({
          defaults: { duration: INFO_DURATION, ease: INFO_EASE },
        });
        applyStack(loc, swapTl);
      }
      activeLoc = loc;
    }

    const stopResize = Resize.add(() => {
      measure();
      if (activeLoc) applyStack(activeLoc, null);
    });

    function destroy() {
      for (const t of colonTweens) t.kill();
      swapTl?.kill();
      stopResize();
    }

    return { tick, destroy, setInitial, swap };
  }

  function setupVisuals(visualM: HTMLElement): VisualsAPI {
    // isolation: isolate keeps per-item z-index bumps from escaping to content-m (next sibling), which would paint over the clock layer.
    gsap.set(visualM, { isolation: "isolate" });

    const items = Array.from(
      visualM.querySelectorAll<HTMLElement>('[office="visual-item"]'),
    );
    type VisualItem = { el: HTMLElement; img: HTMLElement | null };
    const byLocation = new Map<string, VisualItem>();
    for (const item of items) {
      const loc = item.getAttribute("office-location");
      if (!loc) continue;
      byLocation.set(loc, {
        el: item,
        img: item.querySelector<HTMLElement>(".about_office_visual_img"),
      });
    }

    let zCounter = 0;
    let activeLocation: string | undefined;
    let revealTl: gsap.core.Timeline | undefined;

    const SWEEP_MASK =
      "conic-gradient(from var(--sweep-from, 0deg) at 50% 50%, black 0deg, black var(--sweep), transparent var(--sweep))";

    for (const vi of byLocation.values()) {
      gsap.set(vi.el, {
        maskImage: SWEEP_MASK,
        webkitMaskImage: SWEEP_MASK,
        "--sweep": "360deg",
        "--sweep-from": "0deg",
      });
    }

    function minuteHandAngle(loc: string): number {
      const { minute, second, ms } = getTimeInZone(loc);
      const fMinute = minute + (second + ms / 1000) / 60;
      return fMinute * 6;
    }

    return {
      setInitial(loc) {
        const vi = byLocation.get(loc);
        if (!vi) return;
        gsap.set(vi.el, { zIndex: ++zCounter, "--sweep": "360deg" });
        if (vi.img) gsap.set(vi.img, { scale: 1 });
        activeLocation = loc;
      },

      reveal(loc, _direction) {
        if (loc === activeLocation) return;
        const vi = byLocation.get(loc);
        if (!vi) return;

        // Rapid clicks can leave older visuals at a partial --sweep; snap them to fully revealed so they don't bleed through.
        for (const [otherLoc, otherVi] of byLocation) {
          if (otherLoc !== loc && otherLoc !== activeLocation) {
            gsap.set(otherVi.el, { "--sweep": "360deg" });
            if (otherVi.img) gsap.set(otherVi.img, { scale: 1 });
          }
        }

        revealTl?.kill();
        // fromTo applies the from-state synchronously before JS yields, so there's no flash between raise and sweep start.
        gsap.set(vi.el, {
          zIndex: ++zCounter,
          "--sweep-from": `${minuteHandAngle(loc)}deg`,
        });

        if (reduced) {
          gsap.set(vi.el, { "--sweep": "360deg" });
          if (vi.img) gsap.set(vi.img, { scale: 1 });
          if (activeLocation) {
            const prev = byLocation.get(activeLocation);
            if (prev) {
              gsap.set(prev.el, { "--sweep": "360deg" });
              if (prev.img) gsap.set(prev.img, { scale: 1 });
            }
          }
          activeLocation = loc;
          return;
        }

        revealTl = gsap.timeline({
          defaults: { duration: VISUAL_DURATION, ease: VISUAL_EASE },
        });

        if (activeLocation) {
          const prev = byLocation.get(activeLocation);
          if (prev) {
            revealTl.to(
              prev.el,
              {
                "--sweep": "360deg",
                duration: PREV_FINISH_DURATION,
                ease: PREV_FINISH_EASE,
              },
              0,
            );
            if (prev.img) {
              revealTl.to(
                prev.img,
                {
                  scale: 1,
                  duration: PREV_FINISH_DURATION,
                  ease: PREV_FINISH_EASE,
                },
                0,
              );
            }
          }
        }

        revealTl.fromTo(
          vi.el,
          { "--sweep": "0deg" },
          { "--sweep": "360deg" },
          0,
        );
        if (vi.img) {
          revealTl.fromTo(
            vi.img,
            { scale: VISUAL_IMG_SCALE_FROM },
            {
              scale: 1,
              duration: VISUAL_IMG_SCALE_DURATION,
              ease: VISUAL_IMG_SCALE_EASE,
            },
            VISUAL_IMG_SCALE_DELAY,
          );
        }

        activeLocation = loc;
      },

      getPanelEl(loc) {
        return byLocation.get(loc)?.el;
      },
    };
  }

  function setupGroup(
    group: HTMLElement,
    visuals?: VisualsAPI,
    info?: InfoAPI,
  ) {
    const items = Array.from(
      group.querySelectorAll<HTMLElement>('[office="clock-item"]'),
    );
    const bg = group.querySelector<HTMLElement>(".about_clock_bg_move");
    if (!bg || items.length === 0) return;

    let activeItem =
      items.find((i) => i.classList.contains("is--active")) ?? items[0];

    // Lift pill to group level; never re-parenting during tweens avoids containing-block shifts.
    group.insertBefore(bg, items[0]);

    // gsap.set(group, { backgroundColor: GROUP_BG });
    gsap.set(items, { backgroundColor: "transparent" });

    gsap.set(bg, {
      position: "absolute",
      top: 0,
      left: 0,
      right: "auto",
      bottom: "auto",
      pointerEvents: "none",
    });

    let highlighted: HTMLElement = activeItem;
    let tween: gsap.core.Tween | undefined;

    function fitTo(item: HTMLElement, animate: boolean) {
      const groupRect = group.getBoundingClientRect();
      const itemRect = item.getBoundingClientRect();
      const vars = {
        x: itemRect.left - groupRect.left,
        y: itemRect.top - groupRect.top,
        width: itemRect.width,
        height: itemRect.height,
      };

      tween?.kill();
      if (animate && !reduced) {
        tween = gsap.to(bg, {
          ...vars,
          duration: PILL_DURATION,
          ease: PILL_EASE,
        });
      } else {
        gsap.set(bg, vars);
      }
    }

    fitTo(activeItem, false);

    function timezoneOf(item: HTMLElement): string | undefined {
      return (
        item
          .querySelector<HTMLElement>('[office="clock-component"]')
          ?.getAttribute("timezone") ?? undefined
      );
    }

    // tablist / roving-tabindex: Tab enters one item, arrows cycle within the group, Tab exits.
    group.setAttribute("role", "tablist");
    group.setAttribute("aria-label", "Office locations");
    items.forEach((item) => {
      const tz = timezoneOf(item);
      if (!tz) return;
      const tabId = `office-tab-${tz}`;
      const panelId = `office-panel-${tz}`;
      item.id = tabId;
      item.setAttribute("role", "tab");
      item.setAttribute("aria-controls", panelId);
      item.setAttribute("aria-selected", String(item === activeItem));
      item.setAttribute("tabindex", item === activeItem ? "0" : "-1");

      const panel = visuals?.getPanelEl(tz);
      if (panel) {
        panel.id = panelId;
        panel.setAttribute("role", "tabpanel");
        panel.setAttribute("aria-labelledby", tabId);
      }
    });

    function syncAria(active: HTMLElement) {
      items.forEach((it) => {
        const isActive = it === active;
        it.setAttribute("aria-selected", String(isActive));
        it.setAttribute("tabindex", isActive ? "0" : "-1");
      });
    }

    const initialTz = timezoneOf(activeItem);
    if (visuals && initialTz) visuals.setInitial(initialTz);
    if (info && initialTz) info.setInitial(initialTz);

    function highlight(target: HTMLElement) {
      if (target === highlighted) return;

      if (highlighted === activeItem) {
        activeItem.classList.remove("is--active");
      } else {
        highlighted.classList.remove("is--hover");
      }

      if (target === activeItem) {
        activeItem.classList.add("is--active");
      } else {
        target.classList.add("is--hover");
      }

      highlighted = target;
      fitTo(target, true);
    }

    function setActive(item: HTMLElement) {
      if (item === activeItem) return;

      const direction: Direction =
        items.indexOf(item) > items.indexOf(activeItem) ? "right" : "left";

      activeItem.classList.remove("is--active");
      item.classList.remove("is--hover");
      item.classList.add("is--active");
      activeItem = item;
      syncAria(activeItem);

      if (highlighted !== item) {
        highlighted = item;
        fitTo(item, true);
      }

      const tz = timezoneOf(item);
      if (visuals && tz) visuals.reveal(tz, direction);
      if (info && tz) info.swap(tz, direction);

      // Reset the interval so a tap with little remaining time doesn't get immediately overridden by the next auto-advance.
      if (autoTimer !== undefined) startAuto();
    }

    let autoTimer: number | undefined;
    function stopAuto() {
      if (autoTimer !== undefined) {
        window.clearInterval(autoTimer);
        autoTimer = undefined;
      }
    }
    function startAuto() {
      stopAuto();
      if (reduced) return;
      autoTimer = window.setInterval(() => {
        const next = items[(items.indexOf(activeItem) + 1) % items.length];
        setActive(next);
      }, AUTO_INTERVAL_MS);
    }

    const ac = new AbortController();
    // pointerType guard: on touch, pointerenter fires on brief contact and chases the finger across items during a tap, stuttering the pill.
    items.forEach((item) => {
      item.addEventListener(
        "pointerenter",
        (e: PointerEvent) => {
          if (e.pointerType !== "mouse") return;
          highlight(item);
        },
        { signal: ac.signal },
      );
      item.addEventListener("click", () => setActive(item), {
        signal: ac.signal,
      });
    });
    group.addEventListener(
      "pointerenter",
      (e: PointerEvent) => {
        if (e.pointerType !== "mouse") return;
        stopAuto();
      },
      { signal: ac.signal },
    );
    group.addEventListener(
      "pointerleave",
      (e: PointerEvent) => {
        if (e.pointerType !== "mouse") return;
        highlight(activeItem);
        startAuto();
      },
      { signal: ac.signal },
    );

    group.addEventListener(
      "keydown",
      (e) => {
        const target = e.target as HTMLElement;
        const idx = items.indexOf(target);
        if (idx === -1) return;

        let nextIdx = -1;
        switch (e.key) {
          case "ArrowRight":
          case "ArrowDown":
            nextIdx = (idx + 1) % items.length;
            break;
          case "ArrowLeft":
          case "ArrowUp":
            nextIdx = (idx - 1 + items.length) % items.length;
            break;
          case "Home":
            nextIdx = 0;
            break;
          case "End":
            nextIdx = items.length - 1;
            break;
          case "Enter":
          case " ":
            e.preventDefault();
            setActive(target);
            return;
          default:
            return;
        }
        e.preventDefault();
        items[nextIdx].focus();
        setActive(items[nextIdx]);
      },
      { signal: ac.signal },
    );

    // focusout bubbles; only resume auto when focus leaves the group entirely (relatedTarget outside).
    group.addEventListener("focusin", stopAuto, { signal: ac.signal });
    group.addEventListener(
      "focusout",
      (e) => {
        const next = (e as FocusEvent).relatedTarget as Node | null;
        if (!group.contains(next)) startAuto();
      },
      { signal: ac.signal },
    );

    const stopResize = Resize.add(() => fitTo(highlighted, false));

    startAuto();

    cleanups.push(() => {
      ac.abort();
      tween?.kill();
      stopResize();
      stopAuto();
    });
  }

  onMount(() => {
    element
      .querySelectorAll<HTMLElement>('[office="clock-component"]')
      .forEach(setupClock);

    const visualM = element.querySelector<HTMLElement>('[office="visual-m"]');
    const visuals = visualM ? setupVisuals(visualM) : undefined;

    const infoM = element.querySelector<HTMLElement>('[office="info-m"]');
    let info: InfoAPI | undefined;
    if (infoM) {
      info = setupInfo(infoM);
      info.tick(new Date()); // initial render
      const id = window.setInterval(() => info?.tick(new Date()), 1000);
      cleanups.push(() => {
        window.clearInterval(id);
        info?.destroy();
      });
    }

    element
      .querySelectorAll<HTMLElement>('[office="clock-m"]')
      .forEach((group) => setupGroup(group, visuals, info));

    tickClocks();
    stopRaf = Raf.add(tickClocks);
  });

  onDestroy(() => {
    stopRaf?.();
    cleanups.forEach((fn) => fn());
  });
}
